import React, { useEffect, useState } from "react";
import { Card, Row, Spinner, Table } from "react-bootstrap";
import { fetchReportInteractions } from "../../../services/httpClient";

interface InteractionsTableProps {
  data?: any[];
  types?: string[];
  startDate?: string;
  endDate?: string;
  useDatePicker?: boolean;
}

export const InteractionsTable = ({
  data,
  types,
  startDate,
  endDate,
  useDatePicker = false,
}: InteractionsTableProps): React.JSX.Element | null => {
  const [targetTypes, setTargetTypes] = useState<string[]>([]);
  const [tableData, setTableData] = useState<any[]>();
  const columnWidth = 60;

  useEffect(() => {
    const fetchData = async () => {
      if (data && types) {
        setTableData(data);
        setTargetTypes(types);
      } else {
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        let start = starting.toISOString().slice(0, 10);
        let end = new Date().toISOString().slice(0, 10);
        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
        }
        await getInteractions(start!, end!);
      }
    };

    fetchData();
  }, [data, types, startDate, endDate]);

  const getInteractions = async (start: string, end: string) => {
    fetchReportInteractions(start, end)
      .then(async (res: any) => {
        const data = res[0].data;

        const noteTypesArr = data.note_type_counts || [];
        const filteredNoteTypes = noteTypesArr?.filter((t: any) => t?.type !== "system");
        const noteTypeNames = filteredNoteTypes.map((n: any) => n.name);
        setTargetTypes(noteTypeNames);

        const table = data.creator_note_type_totals || [];
        // ensure totals have 0 for missing types
        const normalizedTable = table.map((t: any) => {
          const totals = {} as Record<string, number>;
          for (const nt of noteTypeNames) {
            totals[nt] = t.totals && t.totals[nt] ? t.totals[nt] : 0;
          }
          return { ...t, totals };
        });
        setTableData(normalizedTable);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  if (!tableData) {
    return (
      <Card className="p-3">
        <Card.Body>
          <Row className="text-center">
            <text className="mt-2 mb-2">
              <Spinner animation="border" style={{ color: "#F6893D" }} />
            </text>
          </Row>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <Table striped style={{ tableLayout: "fixed", width: `${columnWidth * targetTypes?.length + 230}px` }}>
          <thead>
            <tr>
              <th style={{ width: "150px" }}>
                <div>
                  <h5 className="app-header-text mb-4" style={{ marginLeft: "-8px" }}>
                    Interactions
                  </h5>
                  <span>Case Manager</span>
                </div>
              </th>
              <th style={{ width: "80px" }}>Total</th>
              {targetTypes?.map((t: string) => (
                <th style={{ width: columnWidth, height: "200px", overflow: "visible" }} key={t}>
                  <div
                    style={{
                      transform: "rotate(-60deg)",
                      transformOrigin: "bottom left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginLeft: "10px",
                      width: "200px",
                    }}
                  >
                    {t}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData?.map((d: any) => (
              <tr key={d.created_by}>
                <td style={{ width: columnWidth }}>{d.creator_firstname + " " + d.creator_lastname}</td>
                <td style={{ width: columnWidth }}>
                  {Object.values(d?.totals as Record<string, number>).reduce((sum, num) => sum + num, 0)}
                </td>
                {Object.keys(d?.totals || {})
                  .sort((a, b) => a.localeCompare(b))
                  .map((key) => (
                    <td key={key} style={{ width: columnWidth }}>
                      {d?.totals[key]}
                    </td>
                  ))}
              </tr>
            ))}
            {tableData.length === 0 && (
              <tr>
                <td colSpan={targetTypes?.length + 2} className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};
